import {
    VerticalTimeline,
    VerticalTimelineElement,
} from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import WorkIcon from "@material-ui/icons/Work";
import { useTheme } from '../context/ThemeContext';

export default function Experience() {
    const { isDarkMode } = useTheme();
    
    const items = [
        {
            date: "Aug 2025 – Present",
            cardTitle: "Research & Development Engineer",
            cardSubtitle: "Kromberg & Schubert s.r.o, Kolarovo, Slovakia",
            cardDetailedText:
                "At Kromberg & Schubert, I develop within automotive research and development, working at the intersection of vehicle technology, embedded systems, and production-minded engineering. The role builds on my robotics research background while bringing the work closer to industrial applications.",
        },
        {
            date: "Sep 2024 – January 2025",
            cardTitle: "Teaching Assistant",
            cardSubtitle: "Széchenyi István University, Győr",
            cardDetailedText:
                "Assisted in modifying and optimizing robotics projects for BSc student curriculum. Worked on autonomous robot development using OpenCV for camera detection, implemented road following algorithms with collision avoidance, and programmed microcontrollers for traffic light detection systems.",
            technologies: ["OpenCV", "Python", "Robotics", "Microcontrollers", "Computer Vision"]
        },
        {
            date: "November 2023 – July 2025",
            cardTitle: "Research Intern - Autonomous Vehicles",
            cardSubtitle: "Vehicle Research Center, Győr",
            cardDetailedText:
                "Developing cutting-edge software for autonomous vehicles and embedded systems. Created GUI visualizations for ROS2 topics, worked on sensor fusion algorithms, and contributed to the autonomous driving software stack for the Lexus RX450 research vehicle.",
            technologies: ["ROS2", "C++", "Python", "Gazebo", "Foxglove", "MATLAB"]
        },
        {
            date: "June 2022 – July 2023",
            cardTitle: "Frontend Developer",
            cardSubtitle: "MynoteBook.com",
            cardDetailedText:
                "Developed and maintained responsive web applications using modern JavaScript frameworks. Implemented user interface designs, optimized application performance, and collaborated with backend teams to integrate APIs and improve user experience.",
            technologies: ["JavaScript", "HTML5", "CSS3", "React", "Git"]
        },
        {
            date: "Jan 2022 – July 2022",
            cardTitle: "Full Stack Developer Intern",
            cardSubtitle: "ASAC (Abdul Aziz Al Ghurair School of Advanced Computing)",
            cardDetailedText:
                "Intensive training program covering full-stack web development. Built multiple projects using React, Node.js, Express, Python, Django, and Flask. Gained experience in data analysis and visualization using NumPy, Matplotlib, and database management.",
            technologies: ["React", "Node.js", "Express", "Python", "Django", "Flask", "NumPy", "Matplotlib"]
        },
        {
            date: "Jun 2021 – Dec 2021",
            cardTitle: "Mechanical Engineering Project",
            cardSubtitle: "Faculty of Engineering Technology",
            cardDetailedText:
                "Led the design and development of mechanical systems for the final year graduation project. Applied engineering principles to solve real-world problems, conducted stress analysis, and created detailed technical documentation.",
            technologies: ["CAD Design", "SolidWorks", "AutoCAD", "Engineering Analysis", "Project Management"]
        }
    ];

    const contentStyle = {
        background: isDarkMode ? "#333333" : "#ffffff",
        color: isDarkMode ? "#ffffff" : "#333333",
        border: `1px solid ${isDarkMode ? "#444444" : "#dee2e6"}`,
        boxShadow: isDarkMode ? "0 3px 10px rgba(0,0,0,0.3)" : "0 3px 10px rgba(0,0,0,0.1)"
    };

    const titleStyle = {
        fontSize: "1.3em",
        fontWeight: "600",
        color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "#333333",
        marginBottom: "8px"
    };

    const subtitleStyle = {
        fontSize: "1.1em",
        fontWeight: "500",
        color: isDarkMode ? "#7e8890" : "#6c757d",
        marginBottom: "12px"
    };

    const textStyle = {
        fontSize: "1em",
        fontWeight: "400",
        color: isDarkMode ? "#b0b0b0" : "#6c757d",
        lineHeight: "1.6",
        marginBottom: "15px"
    };

    const techStyle = {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        marginTop: "10px"
    };

    const techTagStyle = {
        background: isDarkMode ? "#0563bb" : "#e3f2fd",
        color: isDarkMode ? "#ffffff" : "#0563bb",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "0.8em",
        fontWeight: "500"
    };

    return (
        <section id="experience" className="about background-alt">
            <div className="container" data-aos="fade-up">
                <div className="section-title">
                    <span className="section-index">03 — Experience</span>
                    <h2>Engineering in research, education, and industry</h2>
                </div>
                <div className="row">
                    <div className="col-lg-12 d-flex justify-content-center">
                        <VerticalTimeline className="custom-line">
                            {items.map((data, i) => {
                                return (
                                    <VerticalTimelineElement
                                        key={i}
                                        className="vertical-timeline-element--work"
                                        contentStyle={contentStyle}
                                        date={data.date}
                                        dateClassName="timeline-date"
                                        contentArrowStyle={{
                                            borderRight: `7px solid ${isDarkMode ? "#333333" : "#ffffff"}`
                                        }}
                                        iconStyle={{
                                            background: "#0563bb",
                                            color: "#fff",
                                        }}
                                        icon={<WorkIcon />}
                                    >
                                        <h3
                                            className="vertical-timeline-element-title"
                                            style={titleStyle}
                                        >
                                            {data.cardTitle}
                                        </h3>
                                        <h4
                                            className="vertical-timeline-element-subtitle"
                                            style={subtitleStyle}
                                        >
                                            {data.cardSubtitle}
                                        </h4>
                                        <p style={textStyle}>
                                            {data.cardDetailedText}
                                        </p>
                                        {data.technologies && (
                                            <div style={techStyle}>
                                                {data.technologies.map((tech, techIndex) => (
                                                    <span key={techIndex} style={techTagStyle}>
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </VerticalTimelineElement>
                                );
                            })}
                        </VerticalTimeline>
                    </div>
                </div>
            </div>
        </section>
    );
}
